import { useEffect, useState } from "react";
import { decode } from "he";
import { useAppSelector } from "../../app/hooks";
import getWpUrl from "../../utils/getWpUrl";
import { DClub_Date } from "../../apiTypes";
import { ORDER } from "../dates/Panel";

// Réponse de l'endpoint agrégé /dclubagenda/v1/calendar : une seule requête
// contient tout ce que la vue calendrier affiche (au lieu de la cascade
// dates paginées -> artistes, chacune avec résolution ACF complète).
interface CalendarApiDate {
  id: number;
  status: string;
  title: string;
  date: string;
  places: { name: string; slug: string }[];
  artists: { name: string; tracking: string[] }[];
  options: string;
  remarque: string;
}

// Cache de session par plage affichée : revenir sur un mois déjà visité est
// instantané (on sert le cache immédiatement), et la donnée fraîche est
// rechargée en arrière-plan à chaque passage (stale-while-revalidate).
const sessionCache = new Map<string, DClub_Date[]>();

// Reproduit la forme que le rendu du calendrier attend (issue historiquement
// des réponses REST standard + du store artistes).
function toDClubDate(d: CalendarApiDate): DClub_Date {
  return {
    id: d.id,
    status: d.status,
    title: { rendered: d.title },
    date_place: d.places,
    acf: {
      date: d.date,
      tracking_artists: d.artists.map((artist) => ({
        tracking: artist.tracking,
        artiste: { title: { rendered: decode(artist.name) } },
      })),
      date_options: d.options,
      date_remarque_generale: d.remarque,
    },
  } as unknown as DClub_Date;
}

function useCalendarDates(
  start: string,
  end: string,
  enabled: boolean
): [boolean, DClub_Date[]] {
  const account = useAppSelector((state) => state.account);
  const key = `${start}-${end}`;
  const [state, setState] = useState<{
    key: string;
    dates: DClub_Date[];
    loaded: boolean;
  }>({ key: "", dates: [], loaded: false });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const cached = sessionCache.get(key);
    if (cached) {
      setState({ key, dates: cached, loaded: true });
    } else {
      setState((s) =>
        s.key === key ? s : { key, dates: [], loaded: false }
      );
    }

    fetch(`${getWpUrl()}wp-json/dclubagenda/v1/calendar?start=${start}&end=${end}`, {
      headers: {
        Authorization:
          "Basic " + btoa(`${account.user_login}:${account.password}`),
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data: CalendarApiDate[]) => {
        const mapped = data.map(toDClubDate);
        const order = Object.values(ORDER) as string[];
        mapped.sort(
          (a, b) =>
            order.indexOf(a.date_place[0]?.slug ?? "") -
            order.indexOf(b.date_place[0]?.slug ?? "")
        );
        sessionCache.set(key, mapped);
        if (!cancelled) setState({ key, dates: mapped, loaded: true });
      })
      .catch((e) => {
        console.error("calendar endpoint:", e);
        // En cas d'erreur réseau, on garde l'éventuel cache déjà affiché.
        if (!cancelled && !cached)
          setState({ key, dates: [], loaded: true });
      });

    return () => {
      cancelled = true;
    };
  }, [key, enabled, account.user_login, account.password]);

  return [state.loaded && state.key === key, state.dates];
}

export default useCalendarDates;
